import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  addFavorite,
  removeFavorite,
  subscribeToFavorites,
} from '../services/favoritesService'

interface UseFavoritesResult {
  /** Set of teacher uids the current student has favorited. */
  favoriteIds: Set<string>
  /** True while the initial Firestore snapshot is loading. */
  isLoading: boolean
  /** True if the current user is signed in (regardless of userType). */
  isAuthenticated: boolean
  /** Quick membership check. */
  isFavorite: (teacherId: string) => boolean
  /**
   * Toggle a favorite. Performs an optimistic UI update and rolls back
   * if the Firestore write fails. Throws if the user is not signed in
   * — callers should gate on `isAuthenticated` first and prompt sign-in.
   */
  toggleFavorite: (teacherId: string) => Promise<void>
}

/**
 * useFavorites — student-side hook that exposes the current student's
 * favorited teacher list with real-time sync + optimistic toggling.
 *
 * - Subscribes to studentFavorites/{auth.uid} via onSnapshot so changes
 *   in one tab propagate to others.
 * - Maintains an in-memory Set for O(1) membership checks during render.
 * - Optimistic updates: the local Set flips immediately on toggle, then
 *   the Firestore write happens; on failure we revert.
 *
 * If the user is not signed in, `favoriteIds` is always empty and
 * `toggleFavorite` throws. UI should gate clicks on `isAuthenticated`.
 */
export const useFavorites = (): UseFavoritesResult => {
  const { currentUser } = useAuth()
  const studentId = currentUser?.uid || null
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState<boolean>(!!studentId)

  // Track pending optimistic operations so the real-time snapshot
  // doesn't briefly "undo" a click before Firestore has committed.
  const pendingRef = useRef<Map<string, 'add' | 'remove'>>(new Map())

  useEffect(() => {
    if (!studentId) {
      setFavoriteIds(new Set())
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    const unsubscribe = subscribeToFavorites(
      studentId,
      (favorites) => {
        // Merge incoming server state with any pending optimistic ops
        // so a slow snapshot doesn't visually flip a heart back.
        const next = new Set(favorites.teacherIds)
        pendingRef.current.forEach((op, teacherId) => {
          if (op === 'add') next.add(teacherId)
          else next.delete(teacherId)
        })
        setFavoriteIds(next)
        setIsLoading(false)
      },
      () => {
        // On error, stop loading but leave existing state intact.
        setIsLoading(false)
      },
    )
    return () => {
      unsubscribe()
    }
  }, [studentId])

  const isFavorite = useCallback(
    (teacherId: string) => favoriteIds.has(teacherId),
    [favoriteIds],
  )

  const toggleFavorite = useCallback(
    async (teacherId: string) => {
      if (!studentId) {
        throw new Error('Not authenticated')
      }
      if (!teacherId) return

      const currentlyFavorited = favoriteIds.has(teacherId)
      const op: 'add' | 'remove' = currentlyFavorited ? 'remove' : 'add'

      // Optimistic UI flip
      setFavoriteIds((prev) => {
        const next = new Set(prev)
        if (op === 'add') next.add(teacherId)
        else next.delete(teacherId)
        return next
      })
      pendingRef.current.set(teacherId, op)

      try {
        if (op === 'add') {
          await addFavorite(studentId, teacherId)
        } else {
          await removeFavorite(studentId, teacherId)
        }
      } catch (err) {
        // Roll back optimistic update on failure
        console.error('[useFavorites] toggleFavorite failed:', err)
        setFavoriteIds((prev) => {
          const next = new Set(prev)
          if (op === 'add') next.delete(teacherId)
          else next.add(teacherId)
          return next
        })
        throw err
      } finally {
        pendingRef.current.delete(teacherId)
      }
    },
    [studentId, favoriteIds],
  )

  return {
    favoriteIds,
    isLoading,
    isAuthenticated: !!studentId,
    isFavorite,
    toggleFavorite,
  }
}
